import React, { useEffect, useMemo } from "react";
import {
  Button,
  Dropdown,
  Form,
  Header,
  Input,
  Label,
  Menu,
  MenuItem,
  Popup,
  Segment,
  Statistic,
  StatisticLabel,
  StatisticValue,
} from "semantic-ui-react";
import { NotifyTopNav } from "../../../components/NotifyTopNav";
import { useNotifySubscriptionGroups } from "../../../hooks/useNotifySubscriptionGroups";
import {
  NotifySubscribersInfiniteTable,
  NotifySubscribersInfiniteTableType,
  NotifySubscribersInfiniteTableView,
} from "./NotifySubscribersInfiniteTable";
import { useQueryState, parseAsString, parseAsArrayOf, parseAsStringEnum, parseAsInteger, parseAsBoolean } from "nuqs";

import classNames from "classnames";
import PlaceholderUntilLoaded from "../../../../../components/global_components/PlaceholderUntilLoaded";
import { useInfiniteNotifySubscriberContactMethods } from "../../../hooks/useInfiniteNotifySubscriberContactMethods";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { nuqsSortParser } from "../../../util/nuqsSortParser";
import { InviteSubscribersFlyout } from "./flyouts/InviteSubscribersFlyout";
import { NotifyContactsInfiniteTable } from "./NotifyContactInfiniteTable";
import { useInfiniteNotifyContacts } from "../../../hooks/useInfiniteNotifyContacts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";

export function NotifySubscribersPage() {
  const [filterStatus, setFilterStatus] = useQueryState<NotifySubscribersInfiniteTableView>(
    "status",
    parseAsStringEnum(["Enrolled", "Invited", "OptedOut"]).withDefault("Enrolled").withOptions({
      history: "push",
    }),
  );
  const [filterQuery, setFilterQuery] = useQueryState("query", parseAsString.withDefault(""));
  const debouncedFilterQuery = useDebounce(filterQuery, 350);
  const [filterSubscriptionGroups, setFilterSubscriptionGroups] = useQueryState(
    "groupIds",
    parseAsArrayOf(parseAsInteger).withDefault([]),
  );
  const [filterTypes, setFilterTypes] = useQueryState(
    "types",
    parseAsArrayOf<NotifySubscribersInfiniteTableType>(parseAsStringEnum(["Phone", "Email", "App"])).withDefault([]),
  );
  const [sortBy, setSortBy] = useQueryState("sort", nuqsSortParser); // sortBy is set in NotifySubscribersInfiniteTable
  const [defaultToggledSubscriptionGroupIds] = useQueryState(
    "inviteGroupIds",
    parseAsArrayOf(parseAsInteger).withDefault([]),
  ); // For use when opening the InviteSubscribersFlyout via URL action
  const [subscribersFlyoutOpen, setSubscribersFlyoutOpen] = useQueryState(
    "inviteFlyout",
    parseAsBoolean.withDefault(false),
  );

  // Reset sortBy to default whenever filterStatus changes
  useEffect(() => {
    setSortBy([{ id: "createdAt", desc: true }]); // Default sorting
  }, [filterStatus]);

  const { subscriptionGroups, isSubscriptionGroupsLoading } = useNotifySubscriptionGroups({});
  const { subscriberContactMethodsTypeCounts, isSubscriberContactMethodsLoading } =
    useInfiniteNotifySubscriberContactMethods({
      take: 100,
      query: debouncedFilterQuery,
      status: filterStatus,
      subscriptionGroupIds: filterSubscriptionGroups,
      types: filterTypes,
      sort: sortBy?.map(({ id, desc }) => ({ column: id, direction: desc ? "desc" : "asc" })),
    });

  const { notifyContactMethodsTypeCount } = useInfiniteNotifyContacts({
    take: 100,
    query: debouncedFilterQuery,
    status: filterStatus,
    subscriptionGroupIds: filterSubscriptionGroups,
    types: filterTypes,
    sort: sortBy?.map(({ id, desc }) => ({ column: id, direction: desc ? "desc" : "asc" })),
  });

  const filters = useMemo(
    () => ({
      status: filterStatus,
      query: debouncedFilterQuery,
      subscriptionGroups: filterSubscriptionGroups,
      types: filterTypes,
    }),
    [filterStatus, debouncedFilterQuery, filterSubscriptionGroups, filterTypes],
  );

  const getSearchPlaceholder = () => {
    if (filterStatus === "Enrolled") {
      return "Search subscribers";
    }
    if (filterStatus === "Invited") {
      return "Search invitations";
    }
    if (filterStatus === "OptedOut") {
      return "Search opt-outs";
    }
    return "Search";
  };
  const getPeopleToolTipText = () => {
    if (filterStatus === "Enrolled") {
      return "subscribed";
    }
    if (filterStatus === "Invited") {
      return "invited";
    }
    if (filterStatus === "OptedOut") {
      return "opted out";
    }
  };

  return (
    <div>
      <InviteSubscribersFlyout
        open={subscribersFlyoutOpen}
        onClose={() => setSubscribersFlyoutOpen(false)}
        defaultToggledGroupIds={defaultToggledSubscriptionGroupIds}
      />
      <NotifyTopNav />
      <div className='tw-p-3 sm:tw-p-6'>
        <Segment className='tw-flex tw-flex-col tw-gap-3 tw-p-6 tw-transition-all'>
          <div className='tw-flex tw-flex-col tw-justify-between tw-gap-3 md:tw-flex-row md:tw-items-center'>
            <div className='tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-gap-9'>
              <Header className='tw-mb-0' size='large'>
                Subscribers
              </Header>
              <div>
                <Menu compact className='tw-min-h-0 tw-shadow-none'>
                  <MenuItem
                    link
                    className='tw-px-4 tw-py-3'
                    active={filterStatus === "Enrolled"}
                    onClick={() => setFilterStatus("Enrolled")}
                  >
                    Enrolled
                  </MenuItem>
                  <MenuItem
                    link
                    className='tw-px-4 tw-py-3'
                    active={filterStatus === "Invited"}
                    onClick={() => setFilterStatus("Invited")}
                  >
                    Invited
                  </MenuItem>
                  <MenuItem
                    link
                    className='tw-px-4 tw-py-3'
                    active={filterStatus === "OptedOut"}
                    onClick={() => setFilterStatus("OptedOut")}
                  >
                    Opted-out
                  </MenuItem>
                </Menu>
              </div>
            </div>
            <PlaceholderUntilLoaded
              className='-tw-mt-2 tw-w-64'
              isLoading={isSubscriberContactMethodsLoading}
              lineCount={2}
            >
              <div className='tw-flex tw-items-center tw-gap-6'>
                <Statistic size='mini' className='tw-m-0'>
                  <StatisticLabel className='tw-text-xs tw-font-semibold tw-tracking-wider tw-text-grey'>
                    Phone
                  </StatisticLabel>
                  <StatisticValue className={"tw-my-gg-2 tw-text-[15px] tw-font-bold"}>
                    {filterStatus === "Enrolled"
                      ? (notifyContactMethodsTypeCount?.Phone?.toLocaleString() ?? 0)
                      : (subscriberContactMethodsTypeCounts?.Phone?.toLocaleString() ?? 0)}
                  </StatisticValue>
                </Statistic>
                <Statistic size='mini' className='tw-m-0'>
                  <StatisticLabel className='tw-text-xs tw-font-semibold tw-tracking-wider tw-text-grey'>
                    Email
                  </StatisticLabel>
                  <StatisticValue className={"tw-my-gg-2 tw-text-[15px] tw-font-bold"}>
                    {filterStatus === "Enrolled"
                      ? (notifyContactMethodsTypeCount?.Email?.toLocaleString() ?? 0)
                      : (subscriberContactMethodsTypeCounts?.Email?.toLocaleString() ?? 0)}
                  </StatisticValue>
                </Statistic>
                {filterStatus !== "Invited" && (
                  <Statistic size='mini' className='tw-m-0'>
                    <StatisticLabel className='tw-text-xs tw-font-semibold tw-tracking-wider tw-text-grey'>
                      Apps
                    </StatisticLabel>
                    <StatisticValue className={"tw-my-gg-2 tw-text-[15px] tw-font-bold"}>
                      {filterStatus === "Enrolled"
                        ? (notifyContactMethodsTypeCount?.App?.toLocaleString() ?? 0)
                        : (subscriberContactMethodsTypeCounts?.App?.toLocaleString() ?? 0)}
                    </StatisticValue>
                  </Statistic>
                )}
                <Statistic size='mini' className='tw-m-0'>
                  <StatisticLabel className='tw-text-xs tw-font-semibold tw-tracking-wider tw-text-grey'>
                    People
                    <Popup
                      content={
                        <div>
                          <div className='tw-text-primary-hint'>
                            Estimated number of people {getPeopleToolTipText()}. Contacts with multiple contact methods
                            are counted only once.
                          </div>
                        </div>
                      }
                      mouseEnterDelay={300}
                      mouseLeaveDelay={300}
                      on='hover'
                      className='tw-min-w-[350px]'
                      trigger={<FontAwesomeIcon icon={faInfoCircle} className='tw-ml-1' />}
                    />
                  </StatisticLabel>
                  <StatisticValue className={"tw-my-gg-2 tw-text-[15px] tw-font-bold"}>
                    {filterStatus === "Enrolled"
                      ? (notifyContactMethodsTypeCount?.Contact?.toLocaleString() ?? 0)
                      : (subscriberContactMethodsTypeCounts?.Contact.toLocaleString() ?? 0)}
                  </StatisticValue>
                </Statistic>
                <Button secondary content='Invite' onClick={() => setSubscribersFlyoutOpen(true)} />
              </div>
            </PlaceholderUntilLoaded>
          </div>
          <div className='tw-flex tw-flex-col tw-gap-1 md:tw-flex-row md:tw-gap-3'>
            <Form.Field className='tw-m-0'>
              <Input
                className='tw-w-full md:tw-w-64'
                placeholder={getSearchPlaceholder()}
                icon={"search"}
                value={filterQuery}
                onChange={(e, { value }) => setFilterQuery(value, { throttleMs: 500 })}
              />
            </Form.Field>
            <Form.Field className='tw-m-0'>
              <Dropdown
                className={classNames("tw-transition-none", {
                  "md:tw-w-64": filterSubscriptionGroups.length <= 0,
                  "md:tw-w-90": filterSubscriptionGroups.length > 0,
                })}
                options={
                  subscriptionGroups?.map((group) => ({
                    key: group.id,
                    value: group.id,
                    text: group.name,
                  })) ?? []
                }
                placeholder='Subscription group'
                clearable
                loading={isSubscriptionGroupsLoading}
                selectOnBlur={false}
                multiple
                search
                fluid
                selection
                value={filterSubscriptionGroups}
                onChange={(_, { value }) => setFilterSubscriptionGroups(value as number[])}
                renderLabel={(label) => {
                  return <Label className='tw-whitespace-nowrap' content={label.text} />;
                }}
              />
            </Form.Field>
            <Form.Field className='tw-m-0'>
              <Dropdown
                className={classNames("tw-whitespace-nowrap tw-transition-none", {
                  "md:tw-w-28": filterTypes.length <= 0,
                  "md:tw-w-80": filterTypes.length > 0,
                })}
                options={[
                  { key: "Phone", value: "Phone", text: "Phone" },
                  { key: "Email", value: "Email", text: "Email" },
                  { key: "App", value: "App", text: "App" },
                  { key: "Contact", value: "Contact", text: "Contact" },
                ]}
                placeholder='Type'
                clearable
                selectOnBlur={false}
                multiple
                search
                fluid
                selection
                value={filterTypes}
                onChange={(_, { value }) => setFilterTypes(value as NotifySubscribersInfiniteTableType[])}
                renderLabel={(label) => {
                  return <Label className='tw-whitespace-nowrap' content={label.text} />;
                }}
              />
            </Form.Field>
          </div>
        </Segment>
        <div className='tw-h-[calc(100vh-503px)] sm:tw-h-[calc(100vh-324px)] md:tw-h-[calc(100vh-276px)]'>
          {filters.status === "Enrolled" ? (
            <NotifyContactsInfiniteTable filters={filters} />
          ) : (
            <NotifySubscribersInfiniteTable filters={filters} />
          )}
        </div>
      </div>
    </div>
  );
}
